import { VoucherId } from '@df/common';
import { z } from 'zod';
import { zodDecoder } from '$lib/services/api/zod_decoder';

declare const voucherEntryIdBrand: unique symbol;

/**
 * Branded string for a voucher entry id. The wire form is `"{voucherId}:{index}"`,
 * matching the Rust `df_accounting::VoucherEntryId` `SerializeToString` form: the
 * voucher it belongs to plus the index of the row in the original SIE voucher.
 *
 * At runtime a `VoucherEntryId` IS its string, so it keys a plain `Map` by
 * identity (`===`). Operations live on the `VoucherEntryId` namespace value below.
 */
export type VoucherEntryId = string & { readonly [voucherEntryIdBrand]: 'VoucherEntryId' };

/** The single branding cast for the module. */
const brand = (value: string): VoucherEntryId => value as VoucherEntryId;

const voucherEntryStringIdSchema = z.string();

/** A colon-free voucher id, a `:`, then a non-negative integer index. */
const voucherEntryIdPattern = /^[^:]+:\d+$/;

const voucherEntryIdSchema = z.string().transform((str, ctx): VoucherEntryId => {
  if (!voucherEntryIdPattern.test(str)) {
    ctx.addIssue({
      code: 'custom',
      message: `Invalid format for VoucherEntryId: ${str}`,
    });
    return z.NEVER;
  }
  return brand(str);
});

export const VoucherEntryId = {
  make(voucherId: VoucherId, index: number): VoucherEntryId {
    return brand(`${voucherId}:${index}`);
  },

  /** The voucher the entry belongs to. */
  voucherId(id: VoucherEntryId): VoucherId {
    return VoucherId.unsafe(id.slice(0, id.indexOf(':')));
  },

  /** The index of the row within the original SIE voucher. */
  index(id: VoucherEntryId): number {
    return parseInt(id.slice(id.indexOf(':') + 1), 10);
  },

  schema: voucherEntryIdSchema,
};

const voucherEntryIdDecoder = zodDecoder(voucherEntryIdSchema);

export { voucherEntryIdDecoder, voucherEntryIdSchema, voucherEntryStringIdSchema };
